import React, { useCallback, useEffect, useState, useRef } from "react";
import Layout1 from "../../DataLayouts/Layout1";
import NextWeekIcon from "@mui/icons-material/NextWeek";
import LocalPoliceIcon from "@mui/icons-material/LocalPolice";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import EventIcon from "@mui/icons-material/Event";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import axios from "axios";
import toast from "react-hot-toast";
import { MAIN_URL } from "../../../Configurations/Urls";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import Layout4 from "../../DataLayouts/Layout4";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { fetchPayrollComponentTypes } from "../../../Apis/Payroll";
import { operatorRequiresValue } from "../../../Components/filters/operators";

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "payroll_component_type_name",
  sortOrder: "desc",
};

// Helper function to build filters from URL search parameters
const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    )
      return;

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;

      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue
            .split(",")
            .map((v) => v.trim().toLowerCase());
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }

      filters.push({ field, operator, value: filterValue });
    }
  });

  // Handle search parameter
  const search = searchParams.get("search");
  if (search) {
    filters.push({
      field: "payroll_component_type_name",
      operator: "contains",
      value: search,
    });
  }

  return filters;
};
function ComponentTypesList() {
  const [componentTypes, setComponentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const { userData } = useAuthStore();
  const org = userData?.organization;
  const navigate = useNavigate();
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);

  const { id } = useParams();

  // Initialize URL parameters
  useEffect(() => {
    if (hasInitializedURL.current) return;

    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;

    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }

    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  // Fetch data with filters
  useEffect(() => {
    if (!org?.organization_id || !hasInitializedURL.current) return;

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();

    setLoading(true);

    const fetchData = async () => {
      try {
        const filters = buildFiltersFromParams(searchParams);
        const requestBody = {
          filters,
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          sort: {
            field: searchParams.get("sortBy") || "payroll_component_type_name",
            direction: searchParams.get("sortOrder") || "desc",
          },
        };

        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/payroll-component-type/filtered`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        let data;
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            data = result.data;

            if (result.pagination) {
              const pageSize =
                result.pagination.pageSize || result.pagination.limit || 10;
              setPaginationData({
                page:
                  result.pagination.page ||
                  Number.parseInt(searchParams.get("page"), 10) ||
                  1,
                pageSize,
                total: result.pagination.total || 0,
                totalPages: result.pagination.totalPages || 1,
                hasNext: result.pagination.hasNext || false,
                hasPrev: result.pagination.hasPrev || false,
              });
            }
          } else {
            data = [];
            setPaginationData(DEFAULT_PAGINATION);
          }
        } else if (response.status === 404) {
          // Fallback to old GET endpoint if POST endpoint doesn't exist yet
          const oldData = await fetchPayrollComponentTypes(org.organization_id);
          data = oldData?.payrollTypes?.data || oldData?.payrollTypes || [];
          setPaginationData(DEFAULT_PAGINATION);
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Transform data
        let b = data.map((item) => {
          return {
            ...item,
            id: item?.organization_payroll_component_type_id,
            is_active: item?.is_active == 1 ? "✔" : "✖",
          };
        });
        setComponentTypes(b);
      } catch (err) {
        if (err.name === "AbortError") return;

        if (err.response && err.response.status === 401) {
          toast.error("Session Expired!");
          window.location.href = "/login";
        } else {
          console.error("Failed to fetch payroll component types:", err);
          toast.error("Failed to fetch payroll component types");
          setComponentTypes([]);
          setPaginationData(DEFAULT_PAGINATION);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [org, searchParams]);

  const handleSortChange = useCallback(
    (newSortBy, newSortOrder) => {
      setSearchParams(
        (prev) => {
          prev.set("sortBy", newSortBy);
          prev.set("sortOrder", newSortOrder);
          prev.set("page", "1"); // Reset to first page on sort change
          return prev;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  let deleteCalendar = async (id) => {
    try {
      const org_id = org.organization_id;
      const response = await axios.delete(
        `${MAIN_URL}/api/organizations/${org_id}/payroll-component-type/${id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error("Session Expired!");
        window.location.href = "/login";
      }
      console.error("Delete failed:", error);
      toast.error(
        error.response?.data?.error || "Failed to delete Holiday Type"
      );
    }
  };

  const handleEdit = useCallback(

    (item) => {

         navigate(`/payroll/component-types/edit/${item.id}`);

    },

    [navigate]

  );



  return (
    <>
      <Layout4
        loading={loading}
        heading={"Payroll Component Types"}
        btnName={"Add Component Type"}
        Data={componentTypes}
        delete_action={"ORG_CONFIG_DELETE"}
        tableHeaders={[
          {
            name: "Holiday Types",
            value_key: "holiday_type_name",
            textStyle: "capitalize",
          },
          { name: "Description", value_key: "description" },
        ]}
        Icons={[
          <AutorenewIcon sx={{ fontSize: 60, color: "grey.500", mb: 2 }} />,
          <EventIcon color="primary" />,
          <CalendarMonthIcon sx={{ color: "text.secondary" }} />,
        ]}
        messages={[
          "Holiday Type",
          "Holiday Type",
          "Add Holiday Type",
          "Holiday type",
        ]}
        Route={"/payroll/component-types"}
        setData={setComponentTypes}
        DeleteFunc={deleteCalendar}
      />

      <TableDataGeneric
        tableName="organization_payroll_component_types"
        primaryKey="organization_payroll_component_type_id"
        heading="Payroll Component Types"
        data={componentTypes}
        sortname={"payroll_component_type_name"}
        showActions={true}
        Route="/payroll/component-types"
        DeleteFunc={deleteCalendar}
        edit_delete_action={["PAYROLL_COMPONENT_TYPE_DELETE", "PAYROLL_COMPONENT_TYPE_EDIT"]}
        EditFunc={handleEdit}
        token={localStorage.getItem("token")}
        paginationData={paginationData}
        onSortChange={handleSortChange}
        filterModule="organization_payroll_component_types"
      />
    </>
  );
}

export default ComponentTypesList;
